import amplitude from '@autopass-web/mochi-analytics';

import { AMPLITUDE_API_KEY } from '../const';

import type { VehicleType } from '@autopass-web/types';

class AnalyticsService {
  private _plateNumber: string | undefined = undefined;
  private _vehicleType: VehicleType | undefined = undefined;
  private _lineOAChannel: string | undefined = undefined;

  // setter
  public set plateNumber(plateNumber: string) {
    this._plateNumber = plateNumber;
  }

  public set vehicleType(vehicleType: VehicleType) {
    this._vehicleType = vehicleType;
  }

  public set lineOAChannel(lineOAChannel: string) {
    this._lineOAChannel = lineOAChannel;
  }

  // Init and setup functions
  public init = ({ lineOA }: { lineOA: string }) => {
    amplitude.init(AMPLITUDE_API_KEY);
    this._lineOAChannel = lineOA;
  };

  public setUserId = (userId: string) => {
    amplitude.setUserId(userId);
  };

  private _logEvent = (
    eventName: string,
    data: Record<string, unknown> = {},
  ) => {
    amplitude.log(eventName, {
      ...data,
      車牌號碼: this._plateNumber,
      type: this._vehicleType,
      channel: this._lineOAChannel,
    });
  };

  // Intro
  public introPageview = () => {
    this._logEvent('intro_pageview');
  };

  public introStartClick = () => {
    this._logEvent('intro_立即開始_click');
  };

  // Add Plate Number
  public addPlateNumberPageview = () => {
    this._logEvent('車牌頁_pageview');
  };

  public addPlateNumberNextFailedClick = ({ message }: { message: string }) => {
    this._logEvent('車牌頁_下一步_click', {
      message,
      state: '失敗',
    });
  };

  public addPlateNumberNextSucceededClick = () => {
    this._logEvent('車牌頁_下一步_click', {
      state: '成功',
    });
  };

  // Verify Vehicle Registration
  public verifyVehicleRegistrationPageview = () => {
    this._logEvent('行照驗證頁_pageview');
  };

  public verifyVehicleRegistrationUploadClick = () => {
    this._logEvent('行照驗證頁_上傳行照_click');
  };

  public verifyVehicleRegistrationNextClick = () => {
    this._logEvent('行照驗證頁_下一步_click');
  };

  // Verify Vehicle Registration Camera
  public vVRCameraPageview = () => {
    this._logEvent('行照拍攝頁_pageview');
  };

  public vVRCameraFlipClick = () => {
    this._logEvent('行照拍攝頁_切換前後鏡頭_click');
  };

  public vVRCameraCaptureClick = () => {
    this._logEvent('行照拍攝頁_拍攝_click');
  };

  public vVRCameraUploadPhotoClick = () => {
    this._logEvent('行照拍攝頁_上傳照片_click');
  };

  public vVRCameraUploadConfirmDialogPageview = () => {
    this._logEvent('行照上傳確認dialog_pageview');
  };

  public vVRCameraUploadConfirmDialogConfirmClick = () => {
    this._logEvent('行照上傳確認dialog_click');
  };

  // Verify Vehicle Registration Preview
  public vVRPreviewPageview = () => {
    this._logEvent('行照檢查清晰度頁_pageview');
  };

  public vVRPreviewClearClick = () => {
    this._logEvent('行照清晰度確認頁_清晰可辨_click');
  };

  public vVRPreviewRetakeClick = () => {
    this._logEvent('行照清晰度確認頁_重新拍攝_click');
  };

  public vVRPreviewDialogConfirmClick = () => {
    this._logEvent('行照拍攝確認dialog_pageview');
  };

  public vVRPreviewConfirmDialogConfirmClick = ({
    action,
  }: {
    action: 'confirm' | 'cancel';
  }) => {
    this._logEvent('行照拍攝確認dialog_click', {
      type: this._vehicleType,
      車牌號碼: this._plateNumber,
      動作: action === 'confirm' ? '確認' : '取消',
    });
  };

  // Verify Driver License
  public vDLicensePageview = () => {
    this._logEvent('駕照驗證頁_pageview');
  };

  public vDLicenseUploadClick = () => {
    this._logEvent('駕照驗證頁_上傳駕照_click');
  };

  public vDLicenseNextClick = () => {
    this._logEvent('駕照驗證頁_下一步_click');
  };

  // Verify Driver License Front Camera
  public vDLicenseFrontCameraPageview = () => {
    this._logEvent('駕照拍攝正面頁_pageview');
  };

  public vDLicenseFrontCameraFlipClick = () => {
    this._logEvent('駕照拍攝正面頁_切換前後鏡頭_click');
  };

  public vDLicenseFrontCameraCaptureClick = () => {
    this._logEvent('駕照拍攝正面頁_拍攝_click');
  };

  public vDLicenseFrontCameraUploadPhotoClick = () => {
    this._logEvent('駕照拍攝正面頁_上傳照片_click');
  };

  public vDLicenseFrontCameraUploadConfirmDialogPageview = () => {
    this._logEvent('駕照拍攝正面上傳確認dialog_pageview');
  };

  public vDLicenseFrontCameraUploadConfirmDialogConfirmClick = () => {
    this._logEvent('駕照拍攝正面上傳確認dialog_click');
  };

  // Verify Driver License Back Camera
  public vDLicenseBackCameraPageview = () => {
    this._logEvent('駕照拍攝背面頁_pageview');
  };

  public vDLicenseBackCameraFlipClick = () => {
    this._logEvent('駕照拍攝背面頁_切換前後鏡頭_click');
  };

  public vDLicenseBackCameraCaptureClick = () => {
    this._logEvent('駕照拍攝背面頁_拍攝_click');
  };

  public vDLicenseBackCameraUploadPhotoClick = () => {
    this._logEvent('駕照拍攝背面頁_上傳照片_click');
  };

  public vDLicenseBackCameraUploadConfirmDialogPageview = () => {
    this._logEvent('駕照拍攝背面上傳確認dialog_pageview');
  };

  public vDLicenseBackCameraUploadConfirmDialogConfirmClick = () => {
    this._logEvent('駕照拍攝背面上傳確認dialog_click');
  };

  // Verify Driver License Preview
  public vDLPreviewPageview = () => {
    this._logEvent('駕照清晰度確認頁_pageview');
  };

  public vDLPreviewClearClick = () => {
    this._logEvent('駕照清晰度確認頁_清晰可辨_click');
  };

  public vDLPreviewRetakeClick = () => {
    this._logEvent('駕照清晰度確認頁_重新拍攝_click');
  };

  public vDLPreviewUploadConfirmDialogPageview = () => {
    this._logEvent('駕照拍攝確認dialog_pageview');
  };

  public vDLPreviewUploadConfirmDialogConfirmClick = ({
    action,
  }: {
    action: 'confirm' | 'cancel';
  }) => {
    const eventName = action === 'confirm' ? '確認' : '取消';

    this._logEvent('駕照拍攝確認dialog_click', { 動作: eventName });
  };

  // Data Upload
  public dataUploadSucceeded = () => {
    this._logEvent('資料上傳成功頁_pageview');
  };

  public dataUploadFailed = ({ message }: { message: string }) => {
    this._logEvent('資料上傳失敗頁_pageview', {
      errormsg: message,
    });
  };

  // Process
  public processProcessingPageview = () => {
    this._logEvent('處理驗證頁_pageview');
  };

  public processProcessedPageview = () => {
    this._logEvent('處理完成頁_pageview');
  };

  public processProcessingClick = () => {
    this._logEvent('處理驗證頁_稍後回來_click');
  };

  public processProcessedClick = () => {
    this._logEvent('處理完成頁_前往確認_click');
  };

  public processErrorPageview = ({ message }: { message: string }) => {
    this._logEvent('處理完成但有錯誤頁_pageview', {
      errormsg: message,
    });
  };
  public processErrorClick = () => {
    this._logEvent('處理完成但有錯誤頁_重新驗證_click');
  };

  public processErrorManualRefillClick = () => {
    this._logEvent('處理完成但有錯誤頁_手動填寫_click');
  };

  public processErrorContactAutopassClick = () => {
    this._logEvent('處理完成但有錯誤頁_聯繫客服_click');
  };

  // Review
  public reviewPageview = () => {
    this._logEvent('檢查頁_pageview');
  };

  public reviewConfirmClick = () => {
    this._logEvent('檢查頁_確認無誤_click');
  };

  // Verified Success
  public verifiedSuccessPageview = () => {
    this._logEvent('驗證成功頁_pageview');
  };

  public verifiedSuccessCheckDataClick = () => {
    this._logEvent('驗證成功頁_查看驗證資料_click');
  };

  // Check Data
  public checkDataPageview = () => {
    this._logEvent('驗證資訊頁_pageview');
  };

  public checkDataContactAutopassClick = () => {
    this._logEvent('驗證資訊頁_聯繫客服_click');
  };
}

const analyticsService = new AnalyticsService();

export { analyticsService };
